import { usePaymentDetails } from '@/apis/payments/hooks/useGetPaymentDetail'
import PageLoader from '@/app/common/components/PageLoader/PageLoader'
import { DocumentsViewer } from '@/app/common/components/payment_details_items/documents_viewer'
import { PurseMovementCard } from '@/app/common/components/payment_details_items/purse_movements_card'
import PaymentStatusBadge from '@/app/common/components/student_payment_status_badge/payment_status_badge'
import { CheckCircleIcon } from '@/app/common/icons/arrows'
import { FailCircleIcon } from '@/app/common/icons/general'
import { currencyFormatter } from '@/app/common/utilities'
import { FormRowLayout } from '@/app/pages/my_account/components/form_layouts.tsx'
import MarketplaceAppealPopup from '@/app/pages/payments/pages/payment_appeal/_components/marketplace_appeal_popup/marketplace_appeal_popup.tsx'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import { FormRowLabel, FormRowValue } from '@/common/components/FormRow'
import { PaymentType } from '@/schemas/payments.schema'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppealPayProviderAndReimbursement } from './_components/AppealPayProviderAndReimbursement'
import { PaymentItemsList } from './_components/PaymentItemList'
import { ReviewPaymentContainer } from './_components/ReviewPaymentPopup'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import PaymentAmountCard from '@/app/common/components/payment_details_items/payment_amount_card'

type Props = {
  paymentId: string
}

function PaymentDetailsTable({ paymentId }: Props) {
  const programId = useSelector(selectProgramID)
  const { t } = useTranslation()
  const application = useSelector(selectFamilyApplication)

  const query = usePaymentDetails({
    program_id: programId,
    payment_id: paymentId,
  })

  return (
    <PageLoader query={query}>
      {(data) => {
        const shouldDisplayMarketplacePopup =
          data.status === 'Declined by Admin' && data.type === 'Marketplace'

        const comment = data.comments

        function shouldDisplayAppealButton() {
          if (data.has_appealed) {
            return false
          }

          // ONLY REIMBURSEMENTS CAN BE APPEALED FOR 4/14 DEADLINE. THE REST ARE DONE LATER.
          // SPOKEN WITH RIV DIRECTLY
          const appealableTypes: PaymentType[] = [
            'Reimbursement',
            'Pay Provider',
          ]

          return (
            data.status === 'Declined by Admin' &&
            appealableTypes.includes(data.type)
          )
        }

        return (
          <div>
            {shouldDisplayMarketplacePopup && !data.has_appealed && (
              <Card className="mb-4">
                <CardFooter className="flex justify-end items-center">
                  <MarketplaceAppealPopup
                    program_id={data.program_id}
                    payment_id={data.payment_id}
                    success={query.refetch}
                  />
                </CardFooter>
              </Card>
            )}
            {shouldDisplayAppealButton() && (
              <AppealPayProviderAndReimbursement payment={data} />
            )}
            {(data.type === 'Marketplace' || data.type === 'Send Bill') && (
              <ReviewPaymentContainer payment={data} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <div className="flex w-full justify-between items-center">
                    <h2 className="text-lg font-bold">
                      {t('payment_details.title')}
                    </h2>
                    <div className="flex items-center">
                      <p className="font-bold mr-1">
                        {t('payment_details.status')}:
                      </p>

                      <PaymentStatusBadge
                        paymentType={data.type}
                        paymentStatus={data.status}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="">
                    <FormRowLayout>
                      <FormRowLabel text={t('payment_details.payment_id')} />
                      <FormRowValue text={data.payment_id.toString()} />
                    </FormRowLayout>
                    {data.transaction_type && (
                      <FormRowLayout>
                        <FormRowLabel
                          text={t('payment_details.payment_type')}
                        />
                        <FormRowValue text={data.transaction_type} />
                      </FormRowLayout>
                    )}

                    {data.provider_name && (
                      <FormRowLayout>
                        <FormRowLabel
                          text={t('payment_details.provider_name')}
                        />
                        <FormRowValue text={data.provider_name} />
                      </FormRowLayout>
                    )}

                    {data.type === 'Reimbursement' && data.store_name && (
                      <FormRowLayout>
                        <FormRowLabel text="Store Name" />
                        <FormRowValue text={data.store_name} />
                      </FormRowLayout>
                    )}
                    {data.type === 'Send Bill' && (
                      <FormRowLayout>
                        <FormRowLabel text="Allowed Students" />
                        <FormRowValue
                          text={application.students
                            .filter((student) =>
                              data.allowed_students?.includes(
                                student.id.toString()
                              )
                            )
                            .map(
                              (student) =>
                                `${student.first_name} ${student.last_name}`
                            )
                            .join(', ')}
                        />
                      </FormRowLayout>
                    )}

                    <FormRowLayout>
                      <FormRowLabel
                        text={t('payment_details.applicant_name')}
                      />
                      <FormRowValue text={data.applicant_name ?? ''} />
                    </FormRowLayout>

                    <FormRowLayout>
                      <FormRowLabel
                        text={t('payment_details.tracking_number')}
                      />
                      <FormRowValue text={data.tracking_number ?? ''} />
                    </FormRowLayout>
                    <FormRowLayout className="">
                      <FormRowLabel text={t('payment_details.invoice_no')} />
                      <FormRowValue
                        className="max-w-sm "
                        text={data.invoice_quote ?? ''}
                      ></FormRowValue>
                    </FormRowLayout>
                    <FormRowLayout className="flex-col items-start justify-start">
                      <FormRowLabel text={t('your_comments')} />
                      <FormRowValue text={comment ?? ''}></FormRowValue>
                    </FormRowLayout>
                  </div>
                </CardContent>
              </Card>

              <PaymentAmountCard payment={data} />

              {data.expense_categories &&
                data.expense_categories.length > 0 && (
                  <Card>
                    <CardHeader className="text-lg font-bold">
                      Categories
                    </CardHeader>
                    <CardContent>
                      {data.expense_categories.map((category) => {
                        return (
                          <div>
                            <FormRowLayout>
                              <FormRowLabel text={category.name} />
                              <FormRowValue
                                text={currencyFormatter.format(category.amount)}
                              />
                            </FormRowLayout>
                          </div>
                        )
                      })}
                    </CardContent>
                  </Card>
                )}

              {(data.status === 'Declined by Admin' ||
                data.status === 'Declined by Provider' ||
                data.status === 'Appeal Declined by Admin') &&
                data.messages &&
                data.messages?.length > 0 && (
                  <Card>
                    <CardHeader className="text-lg font-bold space-x-2">
                      <FailCircleIcon className="text-red-500 mr-2" />
                      {t('payment_details.rejection_reason')}
                    </CardHeader>
                    <CardContent className="text-sm">
                      {data.messages[data.messages.length - 1].message}
                    </CardContent>
                  </Card>
                )}

              {data.type === 'Marketplace' &&
                [
                  'Approved',
                  'In review Provider',
                  'In review Applicant',
                ].includes(data.status) && (
                  <Card>
                    <CardHeader className="text-lg font-bold space-x-2">
                      <CheckCircleIcon className="text-primary mr-2" />
                      Approval Reason
                    </CardHeader>
                    <CardContent>
                      {Boolean(data.has_appealed) &&
                      data.status !== 'In review Applicant'
                        ? data.last_message
                        : data.admin_review_comment}
                    </CardContent>
                  </Card>
                )}

              {data?.payment_items?.length > 0 && (
                <PaymentItemsList payment_items={data.payment_items} />
              )}

              <PurseMovementCard purses={data.purse_movements ?? []} />

              <DocumentsViewer documents={data.payment_documents} />
            </div>
          </div>
        )
      }}
    </PageLoader>
  )
}

export default PaymentDetailsTable
